import { useEffect, useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import {
  SettingsContentPanel,
  SettingsPageShell,
} from '@terra/ui-design-system';
import { GeneralTab } from './GeneralTab';
import { UpstreamTab } from './UpstreamTab';
import { HeadersTab } from './HeadersTab';
import { SecurityTab } from './SecurityTab';
import { ShortcutsTab } from './settings/ShortcutsTab';
import { BrowserTab } from './BrowserTab';
import { useProxyStore, type SettingsTabId } from '../store/proxy-store';
import { useLayoutStore } from '../store/layoutStore';
import {
  migrateLegacyTabSectionIfNeeded,
  TAB_SECTION_SCOPES,
  useTabSectionState,
} from '../store/tabSections';
import { ClientTlsCertificatesTab } from './settings/ClientTlsCertificatesTab';
import { McpServersTab } from './settings/McpServersTab';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { ActiveSettingsSection } from './settings/ActiveSettingsSection';
import { PROXY_SETTINGS_TAB_HEADERS } from './settings/proxySettingsTabHeaderConfig';
import { SettingsTabHeaderActionsProvider } from './settings/settingsTabHeaderContext';
import {
  getVisibleProxySettingsNavEntries,
  LEGACY_INTERNAL_NETWORK_TUNNEL_SECTION,
  PROXY_SETTINGS_DEFAULT_SECTION,
  resolveVisibleProxySettingsSection,
} from './settings/proxy-settings/proxySettingsNav';
import { ErrorBoundary } from './ErrorBoundary';

type SettingsPageProps = {
  tabId?: string;
  headerAction?: ReactNode;
};

const PROXY_SECTIONS: ReadonlyArray<{
  value: SettingsTabId;
  render: () => ReactNode;
}> = [
  { value: 'general', render: () => <GeneralTab /> },
  { value: 'upstream', render: () => <UpstreamTab /> },
  { value: 'headers', render: () => <HeadersTab /> },
  { value: 'security', render: () => <SecurityTab /> },
  { value: 'browser', render: () => <BrowserTab /> },
  {
    value: 'client-tls-certificates',
    render: () => <ClientTlsCertificatesTab />,
  },
  { value: 'shortcuts', render: () => <ShortcutsTab /> },
];

export function SettingsPage({
  tabId: tabIdProp,
  headerAction,
}: SettingsPageProps) {
  const tProxySettings = useTranslations('settings.proxySettingsActions');
  const tSettings = useTranslations('settings');
  const tabId = useLayoutStore(
    (state) => tabIdProp ?? state.activeMainContentTabId ?? 'proxy-settings',
  );
  const { activeSection: activeTab, setActiveSection: setActiveSettingsTab } =
    useTabSectionState<SettingsTabId>({
      scope: TAB_SECTION_SCOPES.proxySettings,
      tabId,
      defaultSection: PROXY_SETTINGS_DEFAULT_SECTION,
    });
  const { isFeatureFlagEnabled: isMcpServersEnabled } = useFeatureFlags(
    'MCP_SERVERS_ENABLED',
  );

  useEffect(() => {
    migrateLegacyTabSectionIfNeeded({
      scope: TAB_SECTION_SCOPES.proxySettings,
      tabId,
      legacySection: useProxyStore.getState().activeSettingsTab,
      defaultSection: PROXY_SETTINGS_DEFAULT_SECTION,
      clearLegacySection: () =>
        useProxyStore
          .getState()
          .setActiveSettingsTab(PROXY_SETTINGS_DEFAULT_SECTION),
    });
  }, [tabId]);
  useEffect(() => {
    if (!isMcpServersEnabled && activeTab === 'mcp-servers') {
      setActiveSettingsTab(PROXY_SETTINGS_DEFAULT_SECTION);
    }

    if (String(activeTab) === LEGACY_INTERNAL_NETWORK_TUNNEL_SECTION) {
      setActiveSettingsTab('upstream');
    }
  }, [activeTab, isMcpServersEnabled, setActiveSettingsTab]);

  const visibility = useMemo(
    () => ({ isMcpServersEnabled }),
    [isMcpServersEnabled],
  );
  const visibleActiveTab = resolveVisibleProxySettingsSection({
    activeSection: activeTab,
    visibility,
  });
  const navItems = useMemo(
    () =>
      getVisibleProxySettingsNavEntries(visibility).map((entry) => ({
        value: entry.value,
        label: tSettings(`settingsNav.proxy.${entry.labelKey}`),
      })),
    [tSettings, visibility],
  );

  return (
    <SettingsPageShell
      title={tProxySettings('pageTitle')}
      headerAction={headerAction}
      navItems={navItems}
      activeNavItem={visibleActiveTab}
      onNavItemChange={(value) => setActiveSettingsTab(value as SettingsTabId)}
      hideContentScrollbar
    >
      <VStack gap="0" minH={0} alignItems="stretch">
        <SettingsTabHeaderActionsProvider>
          <ActiveSettingsSection
            config={PROXY_SETTINGS_TAB_HEADERS[visibleActiveTab]}
          >
            <ErrorBoundary
              scopeId={`proxy-settings-${visibleActiveTab}`}
              onClose={() =>
                setActiveSettingsTab(PROXY_SETTINGS_DEFAULT_SECTION)
              }
              dismissActionLabel={tSettings('settingsNav.goToMain')}
            >
              {PROXY_SECTIONS.map((section) => (
                <SettingsContentPanel
                  key={section.value}
                  value={section.value}
                  activeValue={visibleActiveTab}
                >
                  {section.render()}
                </SettingsContentPanel>
              ))}

              {isMcpServersEnabled ? (
                <SettingsContentPanel
                  value="mcp-servers"
                  activeValue={visibleActiveTab}
                >
                  <McpServersTab />
                </SettingsContentPanel>
              ) : null}
            </ErrorBoundary>
          </ActiveSettingsSection>
        </SettingsTabHeaderActionsProvider>
      </VStack>
    </SettingsPageShell>
  );
}
